/**
 * Create F&O Positions Dialog
 *
 * Stages a Role Master request to provision N positions for a Job. The request
 * goes to PENDING_APPROVAL; a reviewer approves it on the Requests sub-tab, which
 * provisions the positions to F&O and upserts them locally. Nothing is written to
 * F&O from this dialog — it only stages the request.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Loader2, CheckCircle, AlertTriangle, Layers, Search, X, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from '@/components/ui/sheet';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  jobFamilySyncService, organizationService,
  FoJobListItem, FoPositionListItem, FoPositionProvisionGroup,
} from '@/services/api/organizationService';
import { sastToday } from '@/components/admin/lifecycle/spellCoverage';

// Appointment type → F&O PositionTypeId (mirrors integration employment_mapping).
const APPOINTMENT_TYPES: { value: string; label: string }[] = [
  { value: 'FT Regular', label: 'Permanent (FT Regular)' },
  { value: 'FT Temporary', label: 'Fixed-term (FT Temporary)' },
  { value: 'PT Temporary (IC)', label: 'Independent contractor (PT Temp IC)' },
  { value: 'PT Temporary (Cas)', label: 'Casual (PT Temp Cas)' },
];

// Budget employment category (from RF1 payroll_type) → F&O appointment type. Lines
// with no / unmapped category fall back to the shared default appointment type.
const EMP_CAT_TO_TYPE: Record<string, string> = {
  Permanent: 'FT Regular',
  FTC: 'FT Temporary',
  ICA: 'PT Temporary (IC)',
  Casual: 'PT Temporary (Cas)',
};
const apptLabel = (typeId?: string) =>
  APPOINTMENT_TYPES.find(a => a.value === typeId)?.label || typeId || '—';

const NONE = '__none__';
// The South African day: the UTC day is still yesterday until 02:00 SAST.
const todayIso = () => sastToday();

/** One distinct (dims) batch within a bulk create — each gets its OWN F&O
 *  dimensions. Positions are NEVER created on a shared dim across groups. */
export interface ProvisionGroup {
  title?: string;
  campus?: string;
  department?: string;
  faculty?: string;
  /** Budget employment category (Permanent/FTC/ICA/Casual) — sets this group's
   *  appointment type so a non-permanent line isn't staged as Permanent. */
  employment_category?: string;
  count: number;
}

/** Prefill for opening the dialog from a budget needs-creation line. When the
 *  caller has no F&O Job, the dialog shows an internal Job picker.
 *  When `groups` is set (bulk create from a cohort), the dialog stages ONE
 *  request per group so each position carries its own dims. */
export interface ProvisionSeed {
  title?: string;
  campus?: string;
  department?: string;
  faculty?: string;
  employment_category?: string;
  count?: number;
  business_justification?: string;
  groups?: ProvisionGroup[];
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  job: FoJobListItem | null;
  /** When `job` is null, the dialog lets the operator pick one; `seed` prefills
   *  title/dims/count from the originating budget line(s). */
  seed?: ProvisionSeed | null;
  onStaged?: () => void;
}

const CreateFoPositionsDialog: React.FC<Props> = ({ open, onOpenChange, job, seed, onStaged }) => {
  const [jobs, setJobs] = useState<FoJobListItem[]>([]);
  const [jobSearch, setJobSearch] = useState('');
  const [pickedJob, setPickedJob] = useState<FoJobListItem | null>(null);
  const effJob = job ?? pickedJob;
  // Bulk cohort create: one request per dim-group (each its own dims).
  const grouped = (seed?.groups?.length ?? 0) > 0;
  const [positions, setPositions] = useState<FoPositionListItem[]>([]);
  const [loadingPos, setLoadingPos] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [rtSearch, setRtSearch] = useState('');
  // Grouped (cohort) mode: per-group manual job pick + its search box, for any group
  // whose title doesn't auto-resolve to an F&O job (each group gets its OWN job).
  const [groupOverride, setGroupOverride] = useState<Record<number, FoJobListItem>>({});
  const [groupSearch, setGroupSearch] = useState<Record<number, string>>({});
  // Groups the operator dropped from this batch (by index) — e.g. programme-specific
  // titles that have no real F&O job and shouldn't be created. Excluded groups aren't
  // staged and don't block submit; they stay visible (dimmed) so they can be restored.
  const [excluded, setExcluded] = useState<Set<number>>(new Set());

  const [form, setForm] = useState({
    count: 1,
    title: '',
    description: '',
    department_number: '',
    campus: '',
    department: '',
    faculty: '',
    position_type_id: 'FT Regular',
    reports_to_position_id: '',
    start_date: todayIso(),
    end_date: '',
    business_justification: '',
  });

  useEffect(() => {
    if (open) {
      setResult(null);
      setRtSearch('');
      setPickedJob(null);
      setGroupOverride({});
      setGroupSearch({});
      setExcluded(new Set());
      // Don't prefill the picker in grouped mode — each group resolves its OWN job;
      // a prefill would misleadingly look like "the fallback is the first group".
      setJobSearch(seed?.groups?.length ? '' : (seed?.title || ''));
      setForm({
        count: seed?.count || 1,
        title: seed?.title || job?.description || job?.title_id || '',
        description: '',
        department_number: '',
        campus: seed?.campus || '', department: seed?.department || '', faculty: seed?.faculty || '',
        // Default the appointment type from the budget line's employment category.
        position_type_id: EMP_CAT_TO_TYPE[seed?.employment_category || ''] || 'FT Regular',
        reports_to_position_id: '',
        start_date: todayIso(),
        end_date: '',
        business_justification: seed?.business_justification || '',
      });
      loadPositions();
      if (!job) loadJobs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, job]);

  const loadPositions = async () => {
    setLoadingPos(true);
    try {
      const data = await jobFamilySyncService.listFoPositions();
      setPositions(data.items || []);
    } catch (e) {
      console.error('Failed to load positions for dimensions/reports-to', e);
    } finally {
      setLoadingPos(false);
    }
  };

  const loadJobs = async () => {
    try {
      const data = await jobFamilySyncService.listFoJobs();
      setJobs(data.items || []);
    } catch (e) {
      console.error('Failed to load jobs for picker', e);
    }
  };

  const jobMatches = useMemo(() => {
    const q = jobSearch.trim().toLowerCase();
    if (!q) return [];
    return jobs.filter(j =>
      j.job_id.toLowerCase().includes(q) ||
      (j.description || '').toLowerCase().includes(q) ||
      (j.title_id || '').toLowerCase().includes(q),
    ).slice(0, 12);
  }, [jobSearch, jobs]);

  // Bulk cohort: resolve each group's OWN F&O job from its title (exact match on
  // job_id / description / title_id). Falls back to the optional picked job only when
  // a title has no F&O-job match. This is what stops a mixed-job cohort being
  // collapsed onto a single job (e.g. a Content Writer line staged as LRC Assistant).
  const resolveGroupJob = (title?: string): FoJobListItem | null => {
    const q = (title || '').trim().toLowerCase();
    if (!q) return null;
    return jobs.find(j =>
      (j.job_id || '').toLowerCase() === q ||
      (j.description || '').toLowerCase() === q ||
      (j.title_id || '').toLowerCase() === q,
    ) || null;
  };
  const groupJobs = useMemo(
    // auto-resolve from the group's own title, else the operator's per-group pick.
    () => (seed?.groups ?? []).map((g, i) => resolveGroupJob(g.title) || groupOverride[i] || null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [seed, jobs, groupOverride],
  );
  // A group still blocks submit only while it's unmatched AND not excluded.
  const unresolvedGroups = grouped ? groupJobs.filter((j, i) => !j && !excluded.has(i)).length : 0;
  const allGroups = seed?.groups ?? [];
  const stagedGroupCount = grouped ? allGroups.filter((_, i) => !excluded.has(i)).length : 0;
  const stagedTotal = grouped ? allGroups.reduce((s, g, i) => excluded.has(i) ? s : s + (g.count || 0), 0) : 0;
  const excludedCount = excluded.size;
  // # of unmatched groups not yet excluded — the "Skip all unmatched" target.
  const unmatchedRemaining = grouped ? groupJobs.filter((j, i) => !j && !excluded.has(i)).length : 0;

  const toggleExclude = (i: number) => setExcluded(prev => {
    const next = new Set(prev);
    if (next.has(i)) next.delete(i); else next.add(i);
    return next;
  });
  const skipAllUnmatched = () => setExcluded(prev => {
    const next = new Set(prev);
    allGroups.forEach((_, i) => { if (!groupJobs[i]) next.add(i); });
    return next;
  });
  const restoreAll = () => setExcluded(new Set());

  // Distinct dimension values already in use — drives the dimension selects.
  const dimOptions = useMemo(() => {
    const c = new Set<string>(), d = new Set<string>(), f = new Set<string>();
    for (const p of positions) {
      if (p.dimension_campus) c.add(p.dimension_campus);
      if (p.dimension_department) d.add(p.dimension_department);
      if (p.dimension_faculty) f.add(p.dimension_faculty);
    }
    return {
      campus: Array.from(c).sort(),
      department: Array.from(d).sort(),
      faculty: Array.from(f).sort(),
    };
  }, [positions]);

  const rtMatches = useMemo(() => {
    const q = rtSearch.trim().toLowerCase();
    if (!q) return [];
    return positions.filter(p =>
      p.position_id.toLowerCase().includes(q) ||
      (p.description || '').toLowerCase().includes(q) ||
      (p.worker_name || '').toLowerCase().includes(q),
    ).slice(0, 12);
  }, [rtSearch, positions]);

  const handleSubmit = async () => {
    setSubmitting(true);
    // Form fields shared across every staged request. The JOB is resolved PER GROUP in
    // grouped mode (each group keeps its own job + dims) and from effJob otherwise.
    // Truly shared across every staged request. Job, appointment type, title and
    // description are resolved PER request below (per group in grouped mode).
    const sharedForm = {
      department_number: form.department_number || undefined,
      reports_to_position_id: form.reports_to_position_id || undefined,
      start_date: form.start_date || undefined,
      end_date: form.end_date || undefined,
      business_justification: form.business_justification || undefined,
    };
    const jobFields = (j: FoJobListItem) => ({
      job_id: j.job_id, job_family_id: j.job_family_id || undefined, title_id: j.title_id || undefined,
    });
    try {
      if (grouped) {
        // ONE batch request holding every group — each carries its OWN job, dims and
        // appointment type, but it's a single change request -> a single approval.
        const groups: FoPositionProvisionGroup[] = [];
        seed!.groups!.forEach((g, i) => {
          if (excluded.has(i)) return;  // operator dropped this group from the batch
          const gj = groupJobs[i];
          if (!gj) return;  // unresolved (submit is gated, but guard anyway)
          const gType = EMP_CAT_TO_TYPE[g.employment_category || ''] || form.position_type_id;
          const gTitle = g.title || gj.description || undefined;
          groups.push({
            ...jobFields(gj),
            position_type_id: gType,
            position_type_label: apptLabel(gType),
            count: g.count,
            title: gTitle,
            description: gTitle,  // Description always mirrors the title
            campus: g.campus || undefined,
            department: g.department || undefined,
            faculty: g.faculty || undefined,
          });
        });
        const total = groups.reduce((s, g) => s + (g.count || 0), 0);
        const jobIds = new Set(groups.map(g => g.job_id));
        await organizationService.createPositionProvisionRequest({ ...sharedForm, groups });
        setResult({
          ok: true,
          message: `Staged ONE request for ${total} position(s) across ${jobIds.size} job(s) — each under its own job + dimensions${excludedCount > 0 ? ` (${excludedCount} group(s) skipped)` : ''}. Approve it once on the Requests tab.`,
        });
      } else {
        if (!effJob) { setSubmitting(false); return; }
        await organizationService.createPositionProvisionRequest({
          ...sharedForm, ...jobFields(effJob),
          position_type_id: form.position_type_id,
          position_type_label: apptLabel(form.position_type_id),
          count: form.count,
          title: form.title || undefined,
          description: form.title || undefined,  // Description always mirrors the title
          campus: form.campus || undefined,
          department: form.department || undefined,
          faculty: form.faculty || undefined,
        });
        setResult({
          ok: true,
          message: `Staged a request to provision ${form.count} position(s) for ${effJob.job_id}. It's now pending review on the Requests tab.`,
        });
      }
      onStaged?.();
    } catch (e: any) {
      console.error('Failed to stage position request', e);
      setResult({ ok: false, message: e?.response?.data?.detail || 'Failed to stage the request.' });
    } finally {
      setSubmitting(false);
    }
  };

  const dims: Array<'campus' | 'department' | 'faculty'> = ['campus', 'department', 'faculty'];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className={cn('w-full overflow-y-auto', grouped ? 'sm:max-w-3xl' : 'sm:max-w-lg')}>
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-gray-400" /> Create Positions
          </SheetTitle>
          <SheetDescription className="font-mono">
            {grouped
              ? `${new Set(groupJobs.map((j, i) => j && !excluded.has(i) ? j.job_id : null).filter(Boolean)).size} job(s)`
              : (effJob?.job_id || 'pick a job below')}
          </SheetDescription>
        </SheetHeader>

        {result ? (
          <div className="mt-6 space-y-4">
            <div className={cn('rounded p-4 text-center', result.ok ? 'bg-emerald-50' : 'bg-red-50')}>
              {result.ok
                ? <CheckCircle className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
                : <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-2" />}
              <p className={cn('text-sm', result.ok ? 'text-emerald-800' : 'text-red-700')}>{result.message}</p>
            </div>
            <SheetFooter><Button onClick={() => onOpenChange(false)}>Done</Button></SheetFooter>
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            <div className="bg-blue-50 rounded p-3 text-xs text-blue-700 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>This <strong>stages</strong> a request — positions are only created in F&O once a reviewer approves it on the Requests tab.</div>
            </div>

            {/* Single Job picker — only for a single-line create (non-grouped). In
                grouped mode each group resolves/picks its OWN job in the table below. */}
            {!job && !grouped && (
              <div>
                <label className="text-sm font-medium text-gray-700">F&O Job</label>
                {pickedJob ? (
                  <div className="flex items-center justify-between border rounded px-3 py-2 text-sm">
                    <span><span className="font-mono">{pickedJob.job_id}</span> · {pickedJob.description || pickedJob.title_id || '—'}</span>
                    <Button variant="ghost" size="sm" onClick={() => setPickedJob(null)}>Change</Button>
                  </div>
                ) : (
                  <>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input className="pl-9" placeholder="Search F&O job by ID, title or description"
                        value={jobSearch} onChange={e => setJobSearch(e.target.value)} />
                    </div>
                    {jobMatches.length > 0 && (
                      <div className="border rounded mt-1 max-h-44 overflow-y-auto">
                        {jobMatches.map(j => (
                          <button key={j.job_id} type="button"
                            className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 border-b last:border-0"
                            onClick={() => setPickedJob(j)}>
                            <span className="font-mono">{j.job_id}</span> · {j.description || j.title_id || '—'}
                          </button>
                        ))}
                      </div>
                    )}
                    <p className="mt-1 text-[10px] text-amber-600">Pick the F&O job these positions belong to before staging.</p>
                  </>
                )}
              </div>
            )}

            <div className={cn('grid gap-4', grouped ? 'grid-cols-1' : 'grid-cols-2')}>
              {!grouped && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Number of positions</label>
                  <Input type="number" min={1} max={500} value={form.count}
                    onChange={e => setForm({ ...form, count: Math.max(1, Math.min(500, parseInt(e.target.value) || 1)) })} />
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-gray-700">{grouped ? 'Default appointment type (only for groups with no budget type)' : 'Appointment type'}</label>
                <Select value={form.position_type_id} onValueChange={v => setForm({ ...form, position_type_id: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {APPOINTMENT_TYPES.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Grouped (cohort) mode: one request PER dim-group — each its own dims. */}
            {grouped && (
              <div className="rounded border bg-gray-50 p-3">
                <div className="text-sm font-medium text-gray-700 mb-2 flex items-center justify-between gap-2">
                  <span>
                    {stagedTotal} position(s) across {stagedGroupCount} job + dimension group(s)
                    {excludedCount > 0 && <span className="text-gray-400 font-normal"> · {excludedCount} skipped</span>}
                  </span>
                  {excludedCount > 0 && (
                    <button type="button" onClick={restoreAll}
                      className="inline-flex items-center gap-1 text-[11px] font-normal text-heritage-blue hover:underline">
                      <Undo2 className="h-3 w-3" /> Restore all
                    </button>
                  )}
                </div>
                <div className="max-h-48 overflow-y-auto rounded border bg-white">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 text-gray-500">
                      <tr>
                        <th className="text-left px-2 py-1 font-medium">Job</th>
                        <th className="text-left px-2 py-1 font-medium">Type</th>
                        <th className="text-left px-2 py-1 font-medium">Campus</th>
                        <th className="text-left px-2 py-1 font-medium">Dept</th>
                        <th className="text-left px-2 py-1 font-medium">Faculty</th>
                        <th className="text-right px-2 py-1 font-medium">#</th>
                        <th className="px-2 py-1 w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {seed!.groups!.map((g, i) => {
                        const gj = groupJobs[i];
                        const gType = EMP_CAT_TO_TYPE[g.employment_category || ''] || form.position_type_id;
                        const isOut = excluded.has(i);
                        return (
                          <tr key={i} className={cn('border-t', isOut && 'bg-gray-50 text-gray-400')}>
                            <td className={cn('px-2 py-1 max-w-[200px] truncate', isOut && 'line-through')}>
                              {gj
                                ? <span title={`${gj.job_id} · ${gj.description || gj.title_id || ''}`}>{gj.description || gj.title_id || gj.job_id}</span>
                                : <span className={cn(!isOut && 'text-amber-600')} title={`No F&O job matches "${g.title}" — pick one below or remove it`}>⚠ pick below</span>}
                            </td>
                            <td className={cn('px-2 py-1', isOut && 'line-through')} title={apptLabel(gType)}>
                              {g.employment_category
                                ? g.employment_category
                                : <span className={cn(!isOut && 'text-gray-400')}>default · {apptLabel(form.position_type_id)}</span>}
                            </td>
                            <td className="px-2 py-1">{g.campus || <span className="text-gray-300">—</span>}</td>
                            <td className="px-2 py-1">{g.department || <span className="text-gray-300">—</span>}</td>
                            <td className="px-2 py-1">{g.faculty || <span className="text-gray-300">—</span>}</td>
                            <td className="px-2 py-1 text-right tabular-nums">{g.count}</td>
                            <td className="px-2 py-1 text-right">
                              {isOut
                                ? <button type="button" onClick={() => toggleExclude(i)} title="Restore to this batch"
                                    className="text-heritage-blue hover:underline text-[10px] whitespace-nowrap">Restore</button>
                                : <button type="button" onClick={() => toggleExclude(i)} title="Remove from this batch"
                                    className="text-gray-300 hover:text-red-500"><X className="h-3.5 w-3.5" /></button>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="mt-1.5 flex items-start justify-between gap-2">
                  <p className="text-[10px] text-amber-600">
                    {unresolvedGroups > 0
                      ? `${unresolvedGroups} group(s) below have no matching F&O job — pick one for EACH, or remove the ones you don't want to create.`
                      : 'All remaining groups stage as ONE request (a single approval) — each keeps its own job, dimensions and appointment type.'}
                  </p>
                  {unmatchedRemaining > 0 && (
                    <button type="button" onClick={skipAllUnmatched}
                      className="inline-flex items-center gap-1 text-[10px] font-medium text-red-500 hover:underline whitespace-nowrap flex-shrink-0">
                      <X className="h-3 w-3" /> Skip all {unmatchedRemaining} unmatched
                    </button>
                  )}
                </div>

                {/* Per-group job picker — ONE per unmatched group, so two unmatched
                    titles never share a single fallback. Picking resolves that row. */}
                {unresolvedGroups > 0 && (
                  <div className="mt-2 space-y-2 border-t pt-2">
                    {seed!.groups!.map((g, i) => (groupJobs[i] || excluded.has(i)) ? null : (
                      <div key={i}>
                        <div className="text-[11px] text-gray-600 mb-0.5 flex items-center justify-between gap-2">
                          <span className="min-w-0 truncate">
                            <span className="font-medium">{g.title || '—'}</span>
                            <span className="text-gray-400"> · {[g.campus, g.department, g.faculty].filter(Boolean).join(' · ') || '—'}</span>
                          </span>
                          <button type="button" onClick={() => toggleExclude(i)} title="Remove this group from the batch"
                            className="inline-flex items-center gap-0.5 text-[10px] text-gray-400 hover:text-red-500 whitespace-nowrap flex-shrink-0">
                            <X className="h-3 w-3" /> Remove
                          </button>
                        </div>
                        <div className="relative">
                          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                          <Input className="pl-8 h-7 text-xs" placeholder="Search F&O job by id, title or description"
                            value={groupSearch[i] || ''} onChange={e => setGroupSearch(s => ({ ...s, [i]: e.target.value }))} />
                        </div>
                        {(groupSearch[i] || '').trim() && (
                          <div className="border rounded mt-0.5 max-h-32 overflow-y-auto bg-white">
                            {jobs.filter(j => {
                              const q = (groupSearch[i] || '').trim().toLowerCase();
                              return j.job_id.toLowerCase().includes(q)
                                || (j.description || '').toLowerCase().includes(q)
                                || (j.title_id || '').toLowerCase().includes(q);
                            }).slice(0, 8).map(j => (
                              <button key={j.job_id} type="button"
                                className="w-full text-left px-2 py-1 text-[11px] hover:bg-gray-50 border-b last:border-0"
                                onClick={() => { setGroupOverride(o => ({ ...o, [i]: j })); setGroupSearch(s => ({ ...s, [i]: '' })); }}>
                                <span className="font-mono">{j.job_id}</span> · {j.description || j.title_id || '—'}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {!grouped && (
              <div>
                <label className="text-sm font-medium text-gray-700">Title</label>
                <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Position title" />
              </div>
            )}
            <p className="text-[10px] text-gray-400 -mt-1">
              Each position's Description is set to match its Job Title.
            </p>
            <div>
              <label className="text-sm font-medium text-gray-700">Department number</label>
              <Input value={form.department_number} onChange={e => setForm({ ...form, department_number: e.target.value })} placeholder="F&O DepartmentNumber" />
            </div>

            {!grouped && (
              <>
                <div className="grid grid-cols-3 gap-3">
                  {dims.map(dim => (
                    <div key={dim}>
                      <label className="text-sm font-medium text-gray-700 capitalize">{dim}</label>
                      <Select value={form[dim] || NONE} onValueChange={v => setForm({ ...form, [dim]: v === NONE ? '' : v })}>
                        <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NONE}>—</SelectItem>
                          {dimOptions[dim].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-gray-400 -mt-2">
                  Financial dimensions are optional. {loadingPos ? 'Loading options…' :
                    `${dimOptions.campus.length} campus / ${dimOptions.department.length} dept / ${dimOptions.faculty.length} faculty values in use.`}
                </p>
              </>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Start date</label>
                <Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">End date (optional)</label>
                <Input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Reports to (optional)</label>
              {form.reports_to_position_id ? (
                <div className="flex items-center justify-between border rounded px-3 py-2 text-sm">
                  <span className="font-mono">{form.reports_to_position_id}</span>
                  <Button variant="ghost" size="sm" onClick={() => setForm({ ...form, reports_to_position_id: '' })}>Change</Button>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input className="pl-9" placeholder="Search parent position by ID, title or worker"
                      value={rtSearch} onChange={e => setRtSearch(e.target.value)} />
                  </div>
                  {rtMatches.length > 0 && (
                    <div className="border rounded mt-1 max-h-44 overflow-y-auto">
                      {rtMatches.map(p => (
                        <button key={p.position_id} type="button"
                          className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 border-b last:border-0"
                          onClick={() => { setForm({ ...form, reports_to_position_id: p.position_id }); setRtSearch(''); }}>
                          <span className="font-mono">{p.position_id}</span> · {p.description || '—'}
                          {p.worker_name
                            ? <span className="text-gray-400"> · {p.worker_name}</span>
                            : <span className="text-amber-500"> · vacant</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Business justification (optional)</label>
              <Input value={form.business_justification} onChange={e => setForm({ ...form, business_justification: e.target.value })} />
            </div>

            <SheetFooter className="mt-6">
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={handleSubmit} disabled={submitting || (grouped ? (unresolvedGroups > 0 || stagedGroupCount === 0) : !effJob)}>
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                {grouped && stagedGroupCount > 0 ? `Stage request (${stagedGroupCount} group${stagedGroupCount === 1 ? '' : 's'})` : 'Stage request'}
              </Button>
            </SheetFooter>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CreateFoPositionsDialog;
